import React from 'react';
import { Col } from 'react-bootstrap';
import { BranchStep } from 'dorian-shared/types/branch/BranchStep';
import { useHubOptions } from './hooks/useHubOptions';
import { useHubProgressBars } from './hooks/useHubProgressBars';
import { hasAtLeastOneAlwaysAvailableOption } from './hubValidation';
import { HubOptionsList } from './HubOptions/HubOptionsList';
import { HubSection } from './HubSection';
import { HubSettingsForm } from './HubSettingsForm';
import { ProgressBarsList } from './ProgressBars/ProgressBarsList';
import { BranchSelectItem } from './shared/types';

type StepTypeHubProps = {
  step: BranchStep;
  stepIndex: number;
  updateStep: (index: number, step: BranchStep) => void;
  isDisabled: boolean;
  branches: BranchSelectItem[];
  currentBranchId: number | null;
};

export function StepTypeHub({
  step,
  stepIndex,
  updateStep,
  isDisabled,
  branches,
  currentBranchId,
}: StepTypeHubProps) {
  const {
    hubOptions,
    hubSettings,
    handleHubSettingsChange,
    handleAddOption,
    handleDeleteOptions,
    handleUpdateOption,
    handleDuplicateOption,
    handlePasteOptions,
  } = useHubOptions({ step, stepIndex, updateStep });

  const {
    bars: progressBars,
    isAtLimit: progressBarsAtLimit,
    addBar: handleAddProgressBar,
    updateBar: handleUpdateProgressBar,
    removeBar: handleRemoveProgressBar,
    handleDragEnd: handleProgressBarsDragEnd,
  } = useHubProgressBars({ step, stepIndex, updateStep });

  const settingsSummary = hubSettings
    ? [hubSettings.header.text, hubSettings.subheader?.text].filter(Boolean).join(', ') || undefined
    : undefined;

  const optionsSummary = hubOptions.length > 0
    ? `${hubOptions.length} option${hubOptions.length !== 1 ? 's' : ''}`
    : undefined;

  const optionsErrorText = hubOptions.length > 0 && !hasAtLeastOneAlwaysAvailableOption(hubOptions)
    ? 'Need one option that cannot be hidden or disabled.'
    : undefined;

  const progressBarsSummary = progressBars.length > 0
    ? `${progressBars.length} defined`
    : undefined;

  return (
    <Col style={{ minWidth: 0 }}>
      <HubSection title="Hub Settings" summary={settingsSummary}>
        <HubSettingsForm
          settings={hubSettings}
          onChange={handleHubSettingsChange}
        />
      </HubSection>

      <HubSection title="Progress Bars" summary={progressBarsSummary}>
        <ProgressBarsList
          bars={progressBars}
          isStructuralLocked={isDisabled}
          isAtLimit={progressBarsAtLimit}
          onAdd={handleAddProgressBar}
          onUpdate={handleUpdateProgressBar}
          onRemove={handleRemoveProgressBar}
          onDragEnd={handleProgressBarsDragEnd}
        />
      </HubSection>

      <HubSection title="Hub Options" summary={optionsSummary} errorText={optionsErrorText}>
        <HubOptionsList
          options={hubOptions}
          branches={branches}
          currentBranchId={currentBranchId}
          isDisabled={isDisabled}
          onAdd={handleAddOption}
          onDelete={handleDeleteOptions}
          onUpdate={handleUpdateOption}
          onDuplicate={handleDuplicateOption}
          onPaste={handlePasteOptions}
        />
      </HubSection>
    </Col>
  );
}
